// The data area of a repository (/v1/repositories/{id}/data). Data arrives as
// monthly data drops: files are uploaded into the month's open drop, and a
// seal makes the drop immutable. Builds of the repository's workspaces read
// the newest seal of every month at /data. These routes exist only while the
// server has the data area on; getDataArea tells the two apart.

import { getJSON, optionalJSON, request, requestJSON, sendJSON } from "./http.js";
import { parseError } from "./errors.js";

/** OpenStatus is an open drop's state: files still change, or the seal runs. */
export type OpenStatus = "open" | "sealing";

/** PullStatus is the state of the last S3 pull. */
export type PullStatus = "running" | "succeeded" | "failed";

export interface OpenSummary {
  dropId: string;
  status: OpenStatus;
  files: number;
  bytes: number;
}

export interface SealSummary {
  id: string;
  /** sha256 over the seal's "<sha256>  <path>" lines, sorted by path. */
  digest: string;
  files: number;
  bytes: number;
  sealedAt: string;
}

/** MonthSummary is one month of the data area. */
export interface MonthSummary {
  month: string;
  open: OpenSummary | null;
  latestSeal: SealSummary | null;
  /** How many seals the month has. */
  seals: number;
}

export interface DataFile {
  path: string;
  size: number;
}

export interface OpenDrop {
  dropId: string;
  status: OpenStatus;
  files: DataFile[];
  bytes: number;
}

/** Upload is a pending upload: offset bytes of size have arrived. */
export interface Upload {
  id: string;
  path: string;
  size: number;
  offset: number;
}

export interface Seal extends SealSummary {
  /** The user who sealed; empty when an API key did. */
  sealedBy: string;
  /** The API key that sealed; empty when a user did. */
  sealedByKey: string;
}

export interface MonthDetail {
  month: string;
  open: OpenDrop | null;
  uploads: Upload[];
  seals: Seal[];
}

export interface LastPull {
  at: string;
  status: PullStatus;
  error: string;
  month: string;
}

/** DataSource is the S3 bucket the repository pulls months from. */
export interface DataSource {
  endpoint: string;
  region: string;
  bucket: string;
  prefix: string;
  /** Both repository secrets BINO_S3_ACCESS_KEY_ID and BINO_S3_SECRET_ACCESS_KEY are set. */
  credentials: boolean;
  lastPull: LastPull | null;
}

export type DataSourceInput = Pick<DataSource, "endpoint" | "region" | "bucket" | "prefix">;

// ----- Months -----

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** monthOf formats a date as its UTC month, YYYY-MM. */
export function monthOf(d: Date): string {
  const y = String(d.getUTCFullYear()).padStart(4, "0");
  return `${y}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * monthAllowed applies the server's rule for a month that takes uploads, a
 * seal or a pull: YYYY-MM, from 1970-01 to next month (UTC). The server
 * checks it again.
 */
export function monthAllowed(month: string, now = new Date()): boolean {
  const next = monthOf(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)));
  return MONTH_RE.test(month) && month >= "1970-01" && month <= next;
}

function base(id: string): string {
  return `/v1/repositories/${encodeURIComponent(id)}/data`;
}

function monthPath(id: string, month: string): string {
  return `${base(id)}/${encodeURIComponent(month)}`;
}

/**
 * getDataArea lists the repository's months (viewer+). It resolves to null
 * when the server has the data area off: the route is then not registered,
 * and a GET falls through to the app's index.html.
 */
export function getDataArea(id: string): Promise<{ months: MonthSummary[] } | null> {
  return optionalJSON<{ months: MonthSummary[] }>(base(id));
}

/** getMonth returns a month's open drop, pending uploads and seals (viewer+). */
export function getMonth(id: string, month: string): Promise<MonthDetail> {
  return getJSON<MonthDetail>(monthPath(id, month));
}

/** deleteDataFile removes one file from the month's open drop (member+). */
export async function deleteDataFile(id: string, month: string, path: string): Promise<void> {
  const encoded = path.split("/").map(encodeURIComponent).join("/");
  await request(`${monthPath(id, month)}/files/${encoded}`, { method: "DELETE" });
}

/**
 * sealMonth starts the seal of the month's open drop (member+). It answers
 * at once; the seal id is the drop's id, which shows among the month's seals
 * when the seal is done.
 */
export function sealMonth(id: string, month: string): Promise<{ sealId: string }> {
  return sendJSON<{ sealId: string }>(`${monthPath(id, month)}/seal`, "POST", {});
}

/**
 * pullMonth starts a copy of the month from the S3 source into the month's
 * open drop (member+). It answers at once; getDataSource shows how it ends.
 */
export function pullMonth(id: string, month: string): Promise<{ status: PullStatus }> {
  return sendJSON<{ status: PullStatus }>(`${monthPath(id, month)}/pull`, "POST", {});
}

// ----- Uploads -----

/**
 * createUpload starts an upload into the month's open drop, creating the
 * drop if needed (member+). The same path and size answer the upload the
 * server already holds, so a caller continues from its offset.
 */
export function createUpload(
  id: string,
  month: string,
  path: string,
  size: number,
): Promise<Upload> {
  return sendJSON<Upload>(`${monthPath(id, month)}/uploads`, "POST", { path, size });
}

/** getUpload returns a pending upload with the offset the server has (viewer+). */
export function getUpload(id: string, month: string, uploadId: string): Promise<Upload> {
  return getJSON<Upload>(`${monthPath(id, month)}/uploads/${encodeURIComponent(uploadId)}`);
}

/** deleteUpload drops a pending upload and the bytes it received (member+). */
export async function deleteUpload(id: string, month: string, uploadId: string): Promise<void> {
  await request(`${monthPath(id, month)}/uploads/${encodeURIComponent(uploadId)}`, {
    method: "DELETE",
  });
}

/**
 * putChunk sends the bytes of an upload that start at offset (member+). The
 * answer holds the new offset; complete means the file is in the open drop.
 */
export function putChunk(
  id: string,
  month: string,
  uploadId: string,
  offset: number,
  chunk: Blob,
  signal?: AbortSignal,
): Promise<Upload & { complete: boolean }> {
  return requestJSON<Upload & { complete: boolean }>(
    `${monthPath(id, month)}/uploads/${encodeURIComponent(uploadId)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/octet-stream", "Upload-Offset": String(offset) },
      body: chunk,
      signal,
    },
  );
}

/** CHUNK_BYTES is the chunk size; the server takes up to 64 MiB per request. */
const CHUNK_BYTES = 16 * 1024 * 1024;
// A proxy in front of the server may take less than a full chunk; the upload
// halves the chunk on a 413, the server's chunk_too_large or a proxy's own,
// down to this floor.
const MIN_CHUNK_BYTES = 1024 * 1024;
const RETRY_MS = 2000;
// Failed requests in a row before the upload gives up; a request that moves
// the offset resets the count.
const MAX_RETRIES = 5;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(t);
        reject(signal.reason);
      },
      { once: true },
    );
  });
}

/**
 * uploadFile sends one file into the month's open drop at path and resolves
 * when the server has all of it. It sends the bytes from the offset the
 * server already has, so picking the same file again resumes a broken
 * upload. onProgress gets the byte count the server has confirmed.
 *
 * A request can fail without the upload being lost: on upload_offset_mismatch
 * (an earlier request landed after all, or another tab sends the same file)
 * and after a network error it reads the server's offset and goes on from
 * there, and on upload_busy it waits for the other request to end.
 */
export async function uploadFile(
  id: string,
  month: string,
  path: string,
  file: Blob,
  onProgress: (sent: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const up = await createUpload(id, month, path, file.size);
  let offset = up.offset;
  let chunk = CHUNK_BYTES;
  let resync = false;
  let failures = 0;
  onProgress(offset);
  for (;;) {
    signal?.throwIfAborted();
    try {
      if (resync) {
        offset = (await getUpload(id, month, up.id)).offset;
        resync = false;
        onProgress(offset);
      }
      const end = Math.min(offset + chunk, file.size);
      const res = await putChunk(id, month, up.id, offset, file.slice(offset, end), signal);
      if (res.complete) {
        onProgress(file.size);
        return;
      }
      // An empty chunk that does not complete the upload would be sent
      // again forever.
      if (end === offset) throw new Error("The server did not accept the upload as complete.");
      offset = res.offset;
      failures = 0;
      onProgress(offset);
    } catch (e) {
      if (signal?.aborted) throw e;
      const err = parseError(e);
      const network = err.status === undefined && e instanceof TypeError;
      // A proxy answers 413 with a body of its own, not the server's code.
      const tooLarge = err.code === "chunk_too_large" || err.status === 413;
      const retry =
        err.code === "upload_offset_mismatch" ||
        err.code === "upload_busy" ||
        (tooLarge && chunk > MIN_CHUNK_BYTES) ||
        network;
      if (!retry || ++failures > MAX_RETRIES) throw e;
      if (tooLarge) {
        chunk = Math.max(MIN_CHUNK_BYTES, Math.floor(chunk / 2));
        continue;
      }
      if (err.code === "upload_offset_mismatch" || network) resync = true;
      if (err.code !== "upload_offset_mismatch") await sleep(RETRY_MS * failures, signal);
    }
  }
}

// ----- S3 source -----

/**
 * getDataSource returns the repository's S3 source (viewer+), or null when
 * it has none (404 data_source_not_found).
 */
export function getDataSource(id: string): Promise<DataSource | null> {
  return optionalJSON<DataSource>(`${base(id)}/source`);
}

/**
 * setDataSource creates or replaces the S3 source (maintainer+). An endpoint
 * that is not public HTTPS answers 422 invalid_endpoint. The keys are the
 * repository secrets BINO_S3_ACCESS_KEY_ID and BINO_S3_SECRET_ACCESS_KEY.
 */
export function setDataSource(id: string, input: DataSourceInput): Promise<DataSource> {
  return sendJSON<DataSource>(`${base(id)}/source`, "PUT", input);
}

/** deleteDataSource removes the S3 source (maintainer+); data already pulled stays. */
export async function deleteDataSource(id: string): Promise<void> {
  await request(`${base(id)}/source`, { method: "DELETE" });
}
